import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { LoadingComponent } from '../../loading/loading.component';
import {
  CurrencyPipe,
  DatePipe,
  NgForOf,
  UpperCasePipe,
} from '@angular/common';
import { DayAvailability } from '../../lib/events';
import { ProgressService } from '../../lib/progress.service';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { Day, DayTypeType, Product } from '@shared';

interface DaySlot {
  type: DayTypeType;
  from: Date | null;
  to: Date | null;
  minCost: number;
}

@Component({
  selector: 'app-booking-form-by-date',
  imports: [
    LoadingComponent,
    NgForOf,
    DatePipe,
    UpperCasePipe,
    CurrencyPipe,
  ],
  templateUrl: './booking-form-by-date.component.html',
  styleUrl: './booking-form-by-date.component.css',
})
export class BookingFormByDateComponent {
  private timeslotHeadline = signal<ElementRef | null>(null);
  public selectedDateInGermanLocalTime = computed<string | undefined>(() => {
    if (
      this.progressService.selectedDate() !== undefined &&
      this.progressService.selectedDate() !== null
    ) {
      const date = new Date(this.progressService.selectedDate()!);
      return `${('0' + date.getUTCDate()).slice(-2)}.${('0' + (date.getUTCMonth() + 1)).slice(-2)}.${date.getUTCFullYear()}`;
    }
    return undefined;
  });
  public selectedDateAsDateObject = computed<Date | undefined>(() => {
    if (
      this.progressService.selectedDate() !== undefined &&
      this.progressService.selectedDate() !== null
    ) {
      return new Date(this.progressService.selectedDate()!);
    }
    return undefined;
  });

  @ViewChild('endOfSlotList', { static: false }) set timeslotH(
    element: ElementRef,
  ) {
    console.log('timeslotH called', element);
    if (element) {
      this.timeslotHeadline.set(element);
    }
  }

  public progressService = inject(ProgressService);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public readonly firstCalendarDayWeekday = computed<number>(() => {
    return new Date(this.calendarDays()[0].date).getUTCDay();
  });
  public readonly availableDayTypesForSelectedDate = computed<DaySlot[]>(() => {
    let availableDays: (Day | undefined)[] = [];
    let lodgeSelected = false;
    if (this.progressService.selectedLodge()) {
      console.log('availableDayTypesForSelectedDate: Lodge already selected');
      availableDays = this.progressService.selectedLodge()!.days || [];
      lodgeSelected = true;
    } else {
      availableDays = this.daySlots() || [];
    }

    if (this.progressService.selectedPackage()) {
      availableDays = availableDays.filter(
        (day) =>
          !!day?.packages?.find(
            (pkg) => pkg.id === this.progressService.selectedPackage()?.id,
          ),
      );
    }

    const relevantDay = availableDays?.filter(
      (day) =>
        day &&
        this.progressService.selectedDate() &&
        day.date == this.progressService.selectedDate(),
    );

    console.log('Relevant day', relevantDay);

    const dayTypes = [
      ...new Set<DayTypeType>(
        // @ts-ignore
        relevantDay
          .filter((day) => !!day)
          .filter((day) => day.status != 'unavail')
          .map((day) => day.daytype),
      ),
    ];

    return dayTypes
      .filter((dayType) => !!dayType)
      .map((dayType: DayTypeType): DaySlot => {
        const startTimes = relevantDay
          ?.filter((day) => !!day)
          .filter((day) => day.daytype == dayType)
          .map(
            (day) =>
              day &&
              new Date(
                `${this.progressService.selectedDate()} ${day?.time_from}`,
              ),
          );
        const endTimes = relevantDay
          ?.filter((day) => !!day)
          .filter((day) => day.daytype == dayType)
          .map(
            (day) =>
              day &&
              new Date(
                `${this.progressService.selectedDate()} ${day?.time_to}`,
              ),
          );
        const productsCosts = relevantDay
          ?.filter((day) => !!day)
          .filter((day) => day.daytype == dayType)
          .map((day) => this.getCostForProducts(day.products || []))
          .filter((cost) => cost >= 1);

        const packageCosts = relevantDay
          ?.filter((day) => !!day)
          .filter((day) => day.daytype == dayType)
          .map((day) =>
            day.packages?.map((pkg) =>
              this.getCostForProducts(pkg.products || []),
            ),
          )
          .filter((pkgCost) => pkgCost !== undefined)
          .flat()
          .filter((cost) => cost >= 1);

        const minTime = Math.min(...startTimes.map((date) => date.getTime()));
        const maxTime = Math.max(...endTimes.map((date) => date.getTime()));

        const minProductsCosts = Math.min(...productsCosts);
        const minPackageCosts = Math.min(...packageCosts);
        let minCost = Math.min(minPackageCosts, minProductsCosts);

        if (lodgeSelected && this.progressService.selectedPackage()) {
          minCost = this.getCostForProducts(
            this.progressService.selectedPackage()?.products || [],
          );
        }

        if (lodgeSelected && !this.progressService.selectedPackage()) {
          minCost = minProductsCosts;
        }

        return {
          type: dayType,
          from: minTime ? new Date(minTime) : null,
          to: maxTime ? new Date(maxTime) : null,
          minCost,
        };
      });
  });
  public readonly daySlots = computed<(Day | undefined)[] | undefined>(() => {
    return this.progressService
      .selectedFestival()!
      .lodges!.map((lodge) => lodge.days)
      .filter((day) => day && day?.length > 0)
      .flat();
  });

  getCostForProducts(products: Product[]) {
    return products
      ?.map(
        (product) =>
          +(product.pivot?.amount || 1) *
          (+product.price + +(product.bediengeld || 0)),
      )
      .reduce((prev, cur) => prev + cur, 0);
  }

  public readonly calendarDays = computed<any>(() => {
    const startDate = new Date(
      this.progressService.selectedFestival()?.valid_from_date || '',
    );
    const endDate = new Date(
      this.progressService.selectedFestival()?.valid_to_date || '',
    );
    if (startDate == endDate) {
      return [];
    }
    const daySlots = this.daySlots();

    if (!daySlots) {
      return;
    }

    const result: DayAvailability[] = [];
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    for (
      let currentDay = startDate;
      currentDay <= endDate;
      currentDay.setUTCDate(currentDay.getUTCDate() + 1)
    ) {
      const date = new Date(currentDay);
      result.push(<DayAvailability>{
        date: date,
        availableDaytypes: [
          ...new Set(
            daySlots
              .filter(
                (day) =>
                  day &&
                  new Date(day.date || '').getTime() == currentDay.getTime(),
              )
              .filter(
                (day) =>
                  day &&
                  (!this.progressService.selectedPackage() ||
                    !!day.packages?.find(
                      (pkg) =>
                        pkg.id === this.progressService.selectedPackage()?.id,
                    )),
              )
              .filter((day) => {
                return (
                  day &&
                  day.status == 'avail' &&
                  new Date(day.date || '').getTime() > today.getTime() &&
                  day.packages &&
                  day.packages.length > 0
                );
              })
              .map((day) => {
                console.log(
                  `${day!.date} verfügbar ${day!.daytype}, weil: `,
                  day,
                );
                return day!.daytype;
              }),
          ),
        ],
      });
    }

    return result;
  });

  constructor() {
    effect(() => {
      const targetHtmlElement = this.timeslotHeadline();
      if (
        this.availableDayTypesForSelectedDate().length > 0 &&
        targetHtmlElement !== null
      ) {
        setTimeout(
          () =>
            targetHtmlElement.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            }),
          20,
        );
        setTimeout(
          () =>
            targetHtmlElement.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            }),
          150,
        );
      }
    });
  }

  selectDay(calendarDay: DayAvailability) {
    console.log('Date clicked', calendarDay);

    if (calendarDay.availableDaytypes.length <= 0) {
      console.log('Day cannot be selected');
      return;
    }

    this.progressService.dateSelected(
      `${calendarDay.date.getUTCFullYear()}-${('0' + (calendarDay.date.getUTCMonth() + 1)).slice(-2)}-${('0' + calendarDay.date.getUTCDate()).slice(-2)}`,
    );
  }

  selectDayType(daytype: DayTypeType) {
    if (!this.progressService.selectedDate()) {
      return;
    }

    this.progressService.dayTypeSelected(
      this.progressService.selectedDate()!,
      daytype,
    );

    if (!this.progressService.selectedLodge()) {
      this.router.navigate([
        'booking',
        this.route.snapshot.paramMap.get('eventSlug'),
        'location',
      ]);
    } else {
      this.router.navigate(['booking', 'persons']);
    }
  }

  public translateDayslot(slot: DayTypeType | null, overallCount: number) {
    if (!slot) {
      return null;
    }
    if (overallCount === 1) {
      return 'Reservierung';
    }
    switch (slot) {
      case 'evening':
        return 'Abendreservierung';
      case 'morning':
        return 'Mittagreservierung';
      default:
        return 'Unbekannt';
    }
  }
}
